function finiteNumber(value) {
  if (value === null || value === undefined || value === "") return null;
  const number = Number(value);
  return Number.isFinite(number) ? number : null;
}

export function getFixtureScore(fixture) {
  const state = String(
    fixture?.state?.state ||
      fixture?.state?.short_name ||
      fixture?.state?.developer_name ||
      (typeof fixture?.state === "string" ? fixture.state : "") ||
      fixture?.state_id ||
      ""
  ).toUpperCase();
  if (!["FT", "FT_PEN", "AET"].includes(state)) return null;

  const scores = Array.isArray(fixture?.scores) ? fixture.scores : [];
  let home = null;
  let away = null;

  for (const item of scores) {
    if (String(item?.description || "").toUpperCase() !== "CURRENT") {
      continue;
    }

    const goals = finiteNumber(item?.score?.goals);
    const participant = String(item?.score?.participant || "").toLowerCase();

    if (participant === "home") home = goals;
    if (participant === "away") away = goals;
  }

  return home === null || away === null ? null : { home, away };
}

export function getFixtureGoalLayers(fixture) {
  const expectedHome = finiteNumber(fixture?.goalexp?.home);
  const expectedAway = finiteNumber(fixture?.goalexp?.away);
  const shouldHome = finiteNumber(fixture?.post_match?.should_goals?.home);
  const shouldAway = finiteNumber(fixture?.post_match?.should_goals?.away);

  return {
    actual: getFixtureScore(fixture),
    expected:
      expectedHome === null || expectedAway === null
        ? null
        : { home: expectedHome, away: expectedAway },
    should:
      shouldHome === null || shouldAway === null
        ? null
        : { home: shouldHome, away: shouldAway },
  };
}

export function getFixtureDifferences(fixture) {
  const layers = getFixtureGoalLayers(fixture);
  const difference = (value) =>
    value ? value.home - value.away : null;
  const expectedDiff = difference(layers.expected);
  const actualDiff = difference(layers.actual);
  const shouldDiff = difference(layers.should);
  const performanceVsExpected =
    expectedDiff === null || shouldDiff === null
      ? null
      : shouldDiff - expectedDiff;
  const resultVsExpected =
    expectedDiff === null || actualDiff === null
      ? null
      : actualDiff - expectedDiff;

  return {
    expectedDiff,
    actualDiff,
    shouldDiff,
    performanceVsExpected,
    resultVsExpected,
    performanceIndex:
      performanceVsExpected === null || resultVsExpected === null
        ? null
        : performanceVsExpected + resultVsExpected / 3,
  };
}

function probabilityTriple(fixture, layer) {
  const source =
    layer === "expected"
      ? fixture
      : fixture?.post_match?.should_probs;
  const home = finiteNumber(source?.homewin ?? source?.homewinprob);
  const draw = finiteNumber(source?.draw ?? source?.drawprob);
  const away = finiteNumber(source?.awaywin ?? source?.awaywinprob);

  if (home === null || draw === null || away === null) return null;

  // Existing records use both fractions and percentages.
  const divisor = Math.max(home, draw, away) > 1 ? 100 : 1;
  return { home: home / divisor, draw: draw / divisor, away: away / divisor };
}

function actualOutcome(score) {
  if (!score) return null;
  if (score.home > score.away) return "home";
  if (score.home < score.away) return "away";
  return "draw";
}

export function aggregateMatchday(fixtures) {
  const rows = Array.isArray(fixtures) ? fixtures : [];
  const layers = ["actual", "expected", "should"];
  const result = Object.fromEntries(
    layers.map((key) => [
      key,
      {
        fixtures: 0,
        homeWins: 0,
        draws: 0,
        awayWins: 0,
        homeGoals: 0,
        awayGoals: 0,
        probabilityFixtures: 0,
        expectedProbabilitySum: 0,
        occurredProbabilitySum: 0,
        goalDeviationFixtures: 0,
        goalDeviationSum: 0,
      },
    ])
  );

  for (const fixture of rows) {
    const goals = getFixtureGoalLayers(fixture);

    for (const layer of layers) {
      const value = goals[layer];
      if (!value) continue;
      const target = result[layer];
      target.fixtures += 1;
      target.homeGoals += value.home;
      target.awayGoals += value.away;

      if (layer === "actual") {
        const outcome = actualOutcome(value);
        if (outcome === "home") target.homeWins += 1;
        if (outcome === "draw") target.draws += 1;
        if (outcome === "away") target.awayWins += 1;
      } else {
        const probabilities = probabilityTriple(fixture, layer);
        const outcome = actualOutcome(goals.actual);
        if (probabilities && outcome) {
          target.homeWins += probabilities.home;
          target.draws += probabilities.draw;
          target.awayWins += probabilities.away;
          target.probabilityFixtures += 1;
          target.expectedProbabilitySum +=
            probabilities.home ** 2 +
            probabilities.draw ** 2 +
            probabilities.away ** 2;

          target.occurredProbabilitySum += probabilities[outcome];
        }
      }
    }

    const expectedGoalDeviation = finiteNumber(
      fixture?.expected_goal_deviation
    );
    if (expectedGoalDeviation !== null) {
      result.expected.goalDeviationFixtures += 1;
      result.expected.goalDeviationSum += expectedGoalDeviation;
    }

    if (goals.actual && goals.expected) {
      result.actual.goalDeviationFixtures += 1;
      result.actual.goalDeviationSum +=
        Math.abs(goals.actual.home - goals.expected.home) +
        Math.abs(goals.actual.away - goals.expected.away);
    }

  }

  return result;
}

export function getAverageProbabilities(summary) {
  const count = summary?.probabilityFixtures;
  if (!Number.isFinite(count) || count <= 0) {
    return { expected: null, occurred: null };
  }
  return {
    expected: summary.expectedProbabilitySum / count,
    occurred: summary.occurredProbabilitySum / count,
  };
}

export function getAverageGoalDeviation(summary) {
  const count = summary?.goalDeviationFixtures;
  return Number.isFinite(count) && count > 0
    ? summary.goalDeviationSum / count
    : null;
}

export function getSeasonOccurredModelStatistics(fixtures, expectedLedger) {
  const expectedByFixture = new Map(
    (Array.isArray(expectedLedger) ? expectedLedger : []).map((entry) => [Number(entry.fixture_id), entry])
  );
  let probabilitySum = 0;
  let probabilityCount = 0;
  let deviationSum = 0;
  let deviationCount = 0;

  for (const fixture of Array.isArray(fixtures) ? fixtures : []) {
    const expected = expectedByFixture.get(Number(fixture?.id));
    const score = getFixtureScore(fixture);
    if (!expected || !score) continue;
    const outcome = actualOutcome(score);
    const probabilities = {
      home: finiteNumber(expected.homewinprob),
      draw: finiteNumber(expected.drawprob),
      away: finiteNumber(expected.awaywinprob),
    };
    if (Object.values(probabilities).every((value) => value !== null)) {
      probabilitySum += probabilities[outcome];
      probabilityCount += 1;
    }
    const homeExp = finiteNumber(expected.homegoalexp);
    const awayExp = finiteNumber(expected.awaygoalexp);
    if (homeExp !== null && awayExp !== null) {
      deviationSum += Math.abs(score.home - homeExp) + Math.abs(score.away - awayExp);
      deviationCount += 1;
    }
  }

  return {
    occurred_probability_fixture_count: probabilityCount,
    average_occurred_probability: probabilityCount ? probabilitySum / probabilityCount : null,
    occurred_goal_deviation_fixture_count: deviationCount,
    average_occurred_goal_deviation: deviationCount ? deviationSum / deviationCount : null,
  };
}

export function withSeasonOccurredModelStatistics(comparison, fixtures) {
  const expectedLedger = comparison?.record?.expected?.ledger;
  if (!Array.isArray(expectedLedger)) return comparison;
  return {
    ...comparison,
    record: {
      ...comparison.record,
      model_statistics: {
        ...(comparison.record.model_statistics || {}),
        ...getSeasonOccurredModelStatistics(fixtures, expectedLedger),
      },
    },
  };
}

export function formatOutcomeMetric(summary, outcome, display) {
  const count = summary?.[outcome];
  const fixtures = summary?.fixtures;
  if (!Number.isFinite(count) || !Number.isFinite(fixtures) || fixtures <= 0) return "–";
  if (display === "total") return count.toFixed(Number.isInteger(count) ? 0 : 1);
  const probability = count / fixtures;
  if (display === "odds") return probability > 0 ? (1 / probability).toFixed(2) : "–";
  return `${(probability * 100).toFixed(1)} %`;
}

export function formatGoalMetric(summary, display) {
  const fixtures = summary?.fixtures;
  const home = summary?.homeGoals;
  const away = summary?.awayGoals;
  if (![fixtures, home, away].every(Number.isFinite) || fixtures <= 0) return "–";
  const total = home + away;
  const number = (value) => value.toFixed(Number.isInteger(value) ? 0 : 2);

  if (display === "total") return number(total);
  if (display === "home-away") return `${number(home)} : ${number(away)}`;
  if (display === "home-share") return total > 0 ? `${((home / total) * 100).toFixed(1)} %` : "–";
  if (display === "home-factor") return total > 0 ? (home / (total / 2)).toFixed(2) : "–";
  return (total / fixtures).toFixed(2);
}
